import { TDefaultObjectType } from '@interfaces/common/common';
import { TValueOfStringRecord } from '@interfaces/common/genericWithModifying';
import { TRange } from '@interfaces/common/range';
import { IFlatTreeNodeValidation, IFlatTreeValidationRule, TSubNodesNamesTypes } from '@components/common/FlatTree/interface';

import { getMinMaxDigitalValue, getMinMaxLengthErrMsg } from '@constants/validationConstants';

const INTEGER_REGEX = /^(0|-?[1-9][0-9]*)$/g;
const INTEGER_INPUT_REGEX = /^(-?\d*)$/;
const DECIMAL_REGEX = /^(-)?\d+(.\d+)?$/;
const DECIMAL_INPUT_REGEX = /^(-?\d*(\.\d*)?)?$/;
const ANY_CHARACTER_INPUT_REGEX = /^.*$/;
const ANY_CHARACTER_REGEX = /^.+$/;

const DEFAULT_MIN_LENGTH = 1;
const DEFAULT_MAX_LENGTH = 50;

const BOOLEAN_VALUES = ['true', 'false'];

export const EValidationRuleType = {
	INTERGER_RANGE: 'INTEGER_RANGE',
	CONTAINER: 'CONTAINER',
	AVAILABLE_VALUES: 'AVAILABLE_VALUES',
	DECIMAL_RANGE: 'DECIMAL_RANGE',
	DATA_NODE: 'DATANODE',
	ELEMENTS_RANGE: 'ELEMENTS_RANGE',
	LENGTH_RANGE: 'LENGTH_RANGE',
	PATTERN: 'PATTERN',
	BINARY: 'BINARY',
	EMPTY: 'EMPTY',
	BOOLEAN: 'BOOLEAN',
	GROUP: 'GROUP',
	LIST: 'LIST',
	MANDATORY: 'MANDATORY',
	POSITION_RANGE: 'POSITION_RANGE',
} as const;

export type TValidationRuleType = TValueOfStringRecord<typeof EValidationRuleType>

const DEFAULT_REQUIREMENT_MESSAGE = `
	Value must be between ${DEFAULT_MIN_LENGTH} and ${DEFAULT_MAX_LENGTH} characters long.
	`;

export const DEFAULT_NODE_VALIDATION_ERROR = 'Invalid value';
export const ADD_OBJECT_NODE_ERROR = 'No available objects';
export const ADD_ARRAY_NODE_ERROR = 'Can not add new array node';

const getRequirementsForListRange = (ranges: TRange<number>[]): string => {
	return ranges.map((range) => getMinMaxDigitalValue({
		min: range.from,
		max: range.to,
	})).join('. ');
};

const getValidationForRule = (rule: TDefaultObjectType<IFlatTreeValidationRule>): IFlatTreeNodeValidation => {
	const {
		subNodesNames,
		subNodesNamesTypes,
		decimalRanges = [],
		integerRanges = [],
		lengthRanges = [],
		maxLength,
		minLength,
		pattern,
		enumValues,
	} = rule || {};

	const availableSubNodeNames = subNodesNamesTypes?.length
		? subNodesNamesTypes?.map(({ name }) => name) || []
		: subNodesNames || [];

	const enumNames = enumValues ? enumValues.map(({ name }) => name) : [];

	switch (rule?.type?.name) {
		case EValidationRuleType.POSITION_RANGE:
		case EValidationRuleType.EMPTY:
			return getValidation({
				params: { from: minLength, to: maxLength, pattern },
				validationFunction: validateIsNumberWithinRange,
			});
		case EValidationRuleType.PATTERN:
			return getValidation({
				params: {
					minLength: minLength,
					maxLength: maxLength,
					pattern,
				},
				inputExp: ANY_CHARACTER_INPUT_REGEX,
				errorText: DEFAULT_NODE_VALIDATION_ERROR,
				valueExp: ANY_CHARACTER_REGEX,
				validationFunction: validateString,
				requirements: DEFAULT_REQUIREMENT_MESSAGE,
			});
		case EValidationRuleType.LENGTH_RANGE:
			return getValidation({
				params: {
					minLength: lengthRanges?.length ? lengthRanges[0].from : minLength,
					maxLength: lengthRanges?.length ? lengthRanges[0].to : minLength,
				},
				valueExp: ANY_CHARACTER_INPUT_REGEX,
				inputExp: ANY_CHARACTER_INPUT_REGEX,
				validationFunction: isValidLength,
				requirements: getMinMaxLengthErrMsg('Field', lengthRanges[0].from, lengthRanges[0].to),
			});
		case EValidationRuleType.INTERGER_RANGE:
			return getValidation({
				valueExp: INTEGER_REGEX,
				inputExp: INTEGER_INPUT_REGEX,
				params: { ranges: integerRanges },
				validationFunction: validateListOfRanges,
				requirements: getRequirementsForListRange(integerRanges),
			});
		case EValidationRuleType.DECIMAL_RANGE:
			return getValidation({
				valueExp: DECIMAL_REGEX,
				inputExp: DECIMAL_INPUT_REGEX,
				params: { ranges: decimalRanges },
				validationFunction: validateListOfRanges,
				requirements: getRequirementsForListRange(decimalRanges),
			});
		case EValidationRuleType.AVAILABLE_VALUES:
			return getValidation({
				params: { availableValues: enumNames },
				availableValues: enumNames,
				validationFunction: validateAvailableValues,
				errorText: ADD_OBJECT_NODE_ERROR,
				requirements: `Available values: ${enumNames.join(', ')}`,
			});
		case EValidationRuleType.BOOLEAN:
			return getValidation({
				params: { availableValues: BOOLEAN_VALUES },
				availableValues: BOOLEAN_VALUES,
				validationFunction: validateAvailableValues,
				errorText: ADD_OBJECT_NODE_ERROR,
				requirements: `Available values: ${enumNames.join(', ')}`,
			});
		case EValidationRuleType.CONTAINER:
		case EValidationRuleType.DATA_NODE:
		case EValidationRuleType.LIST:
			return getValidation({
				params: { availableValues: availableSubNodeNames },
				availableValues: availableSubNodeNames,
				availableTypes: subNodesNamesTypes,
				validationFunction: validateAvailableValues,
				errorText: ADD_OBJECT_NODE_ERROR,
				requirements: `Available values: ${availableSubNodeNames.join(', ')}`,
			});
		default:
			return getValidation({
				params: {
					minLength,
					maxLength,
					pattern,
				},
				inputExp: ANY_CHARACTER_INPUT_REGEX,
				errorText: DEFAULT_NODE_VALIDATION_ERROR,
				valueExp: ANY_CHARACTER_REGEX,
				validationFunction: validateString,
				requirements: DEFAULT_REQUIREMENT_MESSAGE,
			});
	}
};

export const getTreeNodeValidation = (
	rules: IFlatTreeValidationRule[],
	isWithDefaultValidation?: boolean,
): IFlatTreeNodeValidation => {
	if (!isWithDefaultValidation) {
		return generateTreeNodeValidation(rules);
	}

	return {
		validateInput: (value: string) => {
			return getValidationForRule({}).validateInput(value);
		},
		validateValue: (value: string) => {
			const validation = getValidationForRule({});

			return validation.validateValue ? validation.validateValue(value) : true;
		},
		requirements: getValidationForRule({}).requirements,
		availableValues: getValidationForRule({}).availableValues,
		availableTypes: getValidationForRule({}).availableTypes,
		errorText: DEFAULT_NODE_VALIDATION_ERROR,
	};
};

export const generateTreeNodeValidation = (
	rules: IFlatTreeValidationRule[],
): IFlatTreeNodeValidation => {
	return {
		validateInput: (value: string) => {
			return rules.every(rule => {
				const validation = getValidationForRule(rule);

				return validation.validateInput(value);
			});
		},
		validateValue: (value: string) => {
			return rules.every(rule => {
				const validation = getValidationForRule(rule);

				return validation.validateValue ? validation.validateValue(value) : true;
			});
		},
		requirements: Array.from(new Set(rules)).map(rule => getValidationForRule(rule).requirements).join(' '),
		availableValues: rules.map(rule => getValidationForRule(rule).availableValues).flat(),
		availableTypes: rules.map(rule => getValidationForRule(rule).availableTypes).flat(),
		errorText: rules.map(rule =>
			getValidationForRule(rule).errorText)
			.find(text => text !== DEFAULT_NODE_VALIDATION_ERROR) || DEFAULT_NODE_VALIDATION_ERROR,
	};
};

const validateInput = ({
	inputExp,
	value,
}: {
	inputExp: RegExp;
	value: string;
}): boolean => {
	const isMatch = new RegExp(inputExp).test(value);

	return value.length <= DEFAULT_MAX_LENGTH && isMatch;
};

const getValidation = <T, >({
	validationFunction,
	inputExp = ANY_CHARACTER_INPUT_REGEX,
	params,
	errorText = DEFAULT_NODE_VALIDATION_ERROR,
	valueExp = ANY_CHARACTER_REGEX,
	requirements = DEFAULT_REQUIREMENT_MESSAGE,
	availableValues,
	availableTypes,
}: {
	validationFunction: (params: T & { value: string }) => boolean;
	inputExp?: RegExp;
	valueExp?: RegExp;
	params: T;
	errorText?: string;
	requirements?: string;
	availableValues?: string[];
	availableTypes?: TSubNodesNamesTypes[];
}): IFlatTreeNodeValidation => {
	return {
		validateInput: (value: string) => validateInput({ inputExp, value }),
		validateValue: (value: string) => !!value.match(valueExp) && validationFunction({ ...params, value }),
		requirements,
		errorText: errorText,
		availableValues: availableValues
			? availableValues
			: [],
		availableTypes: availableTypes || [],
	};
};

const validateAvailableValues = ({
	availableValues,
	value,
}: {
	availableValues: string[];
	value: string;
}): boolean => {
	return !!availableValues.find((item) => item.toString() === value);
};

const validateString = ({
	pattern,
	maxLength = DEFAULT_MAX_LENGTH,
	minLength = DEFAULT_MIN_LENGTH,
	value,
}: {
	pattern?: string;
	maxLength: number;
	minLength: number;
	value: string;
}): boolean => {
	const isMatchPattern = pattern ? new RegExp(pattern).test(value) : true;

	const isWithinLength = value.length <= maxLength && value.length >= minLength;

	return isMatchPattern && isWithinLength;
};

const isValidLength = ({
	maxLength = DEFAULT_MAX_LENGTH,
	minLength = DEFAULT_MIN_LENGTH,
	value,
}: {
	maxLength: number;
	minLength: number;
	value: string;
}): boolean => {
	const isLengthValid = value.length <= maxLength && value.length >= minLength;

	return isLengthValid;
};

const validateListOfRanges = ({
	ranges,
	value,
}: {
	ranges: TRange<number>[];
	value: string;
}): boolean => {
	return ranges.some((range) => validateIsNumberWithinRange({ ...range, value }));
};

const validateIsNumberWithinRange = ({
	from,
	to,
	value,
}: {
	from: number;
	to: number;
	value: string;
}): boolean => {
	const number = Number(value);

	const isWithinRange = number >= from && number <= to;

	return isWithinRange;
};
